import React from 'react';
import { Blk, Row } from './SectorStations';

/* ─── SECTOR SKYLINE ──────────────────────────────────────────────────────
   What stands behind the services: the sector's own landscape.

   ── why this replaced a row of abstract blocks ──

   The background used to be five rectangles of varying height. It carried
   RATE — you could see it moving slower than the foreground — and nothing
   else. A section is not about healthcare because the sky is tinted green; it
   is about healthcare because there is a hospital in it. Abstract massing can
   never do that job however well it is drawn.

   So each sector gets a real landscape, in the same drawn language as the
   installations in front of it:

     01  hospital with its cross and ambulance canopy, ward towers, a helipad
     02  cooling towers, a pylon run, a wind farm, a solar field, a substation
     03  sawtooth halls, chimney stacks, a gantry crane, silos, warehousing
     04  main hall and clock tower, a lecture dome, a library, floodlit pitch

   ── two depths, and why ──

   `deep` sits furthest back: massing only, no detail, palest, and it travels
   slowest. `far` sits in front of it with real structures on it. Between
   them, the stations, and the near props in front of everything — four rates
   across the frame.

   That split is atmospheric perspective, and it is what stops a drawn
   background reading as a sticker: detail and contrast both fall off with
   distance, so the deep layer has to be denied the detail even though it
   would be easy to give it. A far hill drawn as crisply as a near one reads
   as nearer, whatever size it is.

   Each piece is authored to fill roughly one station gap, and the piece is
   chosen by station index, so the landscape varies along the row instead of
   tiling one silhouette over and over.
   ───────────────────────────────────────────────────────────────────────── */

/* ════════════════════════════════════════════════════════════════════════
   01 · HEALTHCARE — a hospital campus
   ════════════════════════════════════════════════════════════════════════ */
const HEALTH = g => [
  // main hospital: long block, entrance canopy, cross on the parapet
  () => (
    <>
      <Blk x={-250} y={-196} w={210} h={196} g={g} tone="far" />
      <Blk x={-52} y={-244} w={116} h={244} g={g} tone="far" />
      <Blk x={76} y={-160} w={150} h={160} g={g} tone="far" />
      {/* the cross — the one mark that says hospital at any size */}
      <rect className="zx-sky__mark" x={-8} y={-286} width="16" height="44" rx="2" />
      <rect className="zx-sky__mark" x={-22} y={-272} width="44" height="16" rx="2" />
      {/* entrance canopy */}
      <Blk x={-120} y={-46} w={150} h={12} g={g} tone="far" r={3} />
      <Row x={-234} y={-176} n={7} gap={28} />
      <Row x={-234} y={-140} n={7} gap={28} />
      <Row x={-36} y={-222} n={4} gap={28} />
      <Row x={-36} y={-186} n={4} gap={28} />
      <Row x={92} y={-140} n={5} gap={28} />
    </>
  ),
  // ward towers with setbacks
  () => (
    <>
      <Blk x={-232} y={-168} w={124} h={168} g={g} tone="far" />
      <Blk x={-96} y={-262} w={104} h={262} g={g} tone="far" />
      <Blk x={-72} y={-296} w={56} h={38} g={g} tone="far" />
      <Blk x={24} y={-206} w={132} h={206} g={g} tone="far" />
      <Blk x={172} y={-124} w={92} h={124} g={g} tone="far" />
      <Row x={-216} y={-148} n={4} gap={28} />
      <Row x={-80} y={-240} n={3} gap={30} />
      <Row x={-80} y={-200} n={3} gap={30} />
      <Row x={40} y={-184} n={4} gap={30} />
      <Row x={40} y={-146} n={4} gap={30} />
    </>
  ),
  // ambulance bay under a long canopy
  () => (
    <>
      <Blk x={-240} y={-140} w={190} h={140} g={g} tone="far" />
      <Blk x={-16} y={-116} w={230} h={116} g={g} tone="far" />
      <Blk x={-30} y={-134} w={258} h={14} g={g} tone="far" r={3} />
      {[10, 90, 170].map(x => (
        <rect key={x} x={x} y="-120" width="5" height="120" className="zx-blk__mast" />
      ))}
      <Row x={-224} y={-120} n={6} gap={28} />
      <Row x={-224} y={-84} n={6} gap={28} />
    </>
  ),
  // helipad tower
  () => (
    <>
      <Blk x={-208} y={-146} w={112} h={146} g={g} tone="far" />
      <Blk x={-80} y={-288} w={124} h={288} g={g} tone="far" />
      <ellipse className="zx-sky__mark" cx="-18" cy="-296" rx="76" ry="13" />
      <Blk x={60} y={-176} w={150} h={176} g={g} tone="far" />
      <Row x={-64} y={-266} n={4} gap={28} />
      <Row x={-64} y={-228} n={4} gap={28} />
      <Row x={76} y={-156} n={4} gap={30} />
    </>
  ),
  // clinic row
  () => (
    <>
      <Blk x={-244} y={-118} w={140} h={118} g={g} tone="far" />
      <Blk x={-92} y={-152} w={124} h={152} g={g} tone="far" />
      <Blk x={44} y={-104} w={116} h={104} g={g} tone="far" />
      <Blk x={172} y={-166} w={82} h={166} g={g} tone="far" />
      <Row x={-228} y={-98} n={5} gap={26} />
      <Row x={-76} y={-132} n={4} gap={28} />
      <Row x={60} y={-84} n={4} gap={26} />
    </>
  ),
];

/* ════════════════════════════════════════════════════════════════════════
   02 · UTILITIES & ENERGY — a generating landscape
   ════════════════════════════════════════════════════════════════════════ */
const ENERGY = g => [
  // cooling towers and the turbine hall
  () => (
    <>
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -230 0 L -206 -196 q 26 -22 52 0 L -130 0 Z" />
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -96 0 L -76 -164 q 22 -18 44 0 L -8 0 Z" />
      <Blk x={30} y={-124} w={210} h={124} g={g} tone="far" />
      <rect x={92} y={-208} width="16" height="84" className="zx-blk__mast" />
      <rect x={142} y={-186} width="13" height="62" className="zx-blk__mast" />
      <Row x={48} y={-104} n={6} gap={30} />
    </>
  ),
  // a run of pylons with catenaries
  () => (
    <>
      <path className="zx-sky__wire" d="M -280 -178 Q -140 -128 0 -178 Q 140 -128 280 -178" />
      <path className="zx-sky__wire" d="M -280 -152 Q -140 -104 0 -152 Q 140 -104 280 -152" />
      {[-190, 0, 190].map(x => (
        <g key={x}>
          <path className="zx-blk__mast"
            d={`M ${x - 34} 0 L ${x - 11} -190 L ${x - 4} -190 L ${x - 20} 0 Z`} />
          <path className="zx-blk__mast"
            d={`M ${x + 34} 0 L ${x + 11} -190 L ${x + 4} -190 L ${x + 20} 0 Z`} />
          <rect className="zx-blk__mast" x={x - 46} y="-182" width="92" height="4" />
          <rect className="zx-blk__mast" x={x - 32} y="-206" width="64" height="4" />
          <rect className="zx-blk__mast" x={x - 22} y="-140" width="44" height="3.5" />
        </g>
      ))}
    </>
  ),
  // wind farm
  () => (
    <>
      {[[-180, 232], [-20, 288], [140, 204]].map(([x, hgt], i) => (
        <g key={i}>
          <path className="zx-blk__mast"
            d={`M ${x - 8} 0 L ${x - 3} ${-hgt} L ${x + 3} ${-hgt} L ${x + 8} 0 Z`} />
          <circle className="zx-blk__mast" cx={x} cy={-hgt} r="6" />
          {[15, 135, 255].map(a => (
            <rect key={a} className="zx-sky__blade"
              x={x - 3.5} y={-hgt - 96} width="7" height="96" rx="3.5"
              transform={`rotate(${a + i * 20} ${x} ${-hgt})`} />
          ))}
        </g>
      ))}
      <Blk x={200} y={-84} w={70} h={84} g={g} tone="far" />
    </>
  ),
  // solar field with inverter huts
  () => (
    <>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <path key={i} className="zx-sky__panel"
          d={`M ${-260 + i * 88} 0 L ${-242 + i * 88} -62 L ${-186 + i * 88} -62
              L ${-204 + i * 88} 0 Z`} />
      ))}
      {[0, 1, 2, 3, 4, 5].map(i => (
        <rect key={i} className="zx-blk__mast"
          x={-236 + i * 88} y="-34" width="4" height="34" />
      ))}
      <Blk x={196} y={-92} w={74} h={92} g={g} tone="far" />
      <Row x={210} y={-74} n={2} gap={24} />
    </>
  ),
  // substation yard
  () => (
    <>
      <Blk x={-250} y={-104} w={104} h={104} g={g} tone="far" r={3} />
      <Blk x={-124} y={-88} w={92} h={88} g={g} tone="far" r={3} />
      {[-224, -170, -98, -50].map(x => (
        <React.Fragment key={x}>
          <rect className="zx-blk__mast" x={x} y="-142" width="11" height="46" />
          <circle className="zx-sky__mark" cx={x + 5} cy="-148" r="5" />
        </React.Fragment>
      ))}
      <rect className="zx-blk__mast" x={-262} y="-168" width="248" height="4" />
      <Blk x={40} y={-136} w={132} h={136} g={g} tone="far" />
      <Row x={56} y={-116} n={4} gap={28} />
    </>
  ),
];

/* ════════════════════════════════════════════════════════════════════════
   03 · INDUSTRIAL IoT — a working plant
   ════════════════════════════════════════════════════════════════════════ */
const INDUSTRIAL = g => [
  // sawtooth halls
  () => (
    <>
      <Blk x={-260} y={-136} w={330} h={136} g={g} tone="far" r={2} />
      {[0, 1, 2, 3, 4].map(i => (
        <path key={i} className="zx-blk zx-blk--far" fill={`url(#${g})`}
          d={`M ${-260 + i * 66} -136 L ${-260 + i * 66} -180 L ${-194 + i * 66} -136 Z`} />
      ))}
      <Blk x={92} y={-104} w={166} h={104} g={g} tone="far" r={2} />
      <Row x={-244} y={-112} n={9} gap={34} />
      <Row x={108} y={-84} n={4} gap={34} />
    </>
  ),
  // chimney stacks over a boiler house
  () => (
    <>
      <Blk x={-236} y={-120} w={200} h={120} g={g} tone="far" r={2} />
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -150 -120 L -140 -290 L -116 -290 L -106 -120 Z" />
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -70 -120 L -63 -236 L -45 -236 L -38 -120 Z" />
      <rect className="zx-sky__mark" x={-146} y="-296" width="36" height="7" rx="2" />
      <Blk x={0} y={-152} w={124} h={152} g={g} tone="far" r={2} />
      <Blk x={140} y={-96} w={118} h={96} g={g} tone="far" r={2} />
      <Row x={-220} y={-100} n={5} gap={32} />
      <Row x={16} y={-132} n={3} gap={34} />
    </>
  ),
  // gantry crane over a yard
  () => (
    <>
      <rect className="zx-blk__mast" x={-206} y="-206" width="11" height="206" />
      <rect className="zx-blk__mast" x={126} y="-206" width="11" height="206" />
      <rect className="zx-blk__mast" x={-224} y="-220" width="378" height="14" />
      <rect className="zx-blk__mast" x={-40} y="-206" width="8" height="72" />
      <Blk x={-70} y={-134} w={68} h={30} g={g} tone="far" r={3} />
      <Blk x={-250} y={-84} w={112} h={84} g={g} tone="far" r={2} />
      <Blk x={170} y={-116} w={92} h={116} g={g} tone="far" r={2} />
      <Row x={184} y={-98} n={3} gap={26} />
    </>
  ),
  // silo cluster
  () => (
    <>
      {[-210, -140, -70].map(x => (
        <Blk key={x} x={x} y={-186} w={58} h={186} g={g} tone="far" r={22} />
      ))}
      <Blk x={-216} y={-206} w={202} h={22} g={g} tone="far" r={4} />
      <Blk x={30} y={-124} w={160} h={124} g={g} tone="far" r={2} />
      <rect className="zx-blk__mast" x={214} y="-230" width="8" height="230" />
      <circle className="zx-sky__mark" cx="218" cy="-236" r="6" />
      <Row x={46} y={-104} n={4} gap={34} />
    </>
  ),
  // warehousing and loading docks
  () => (
    <>
      <Blk x={-256} y={-112} w={286} h={112} g={g} tone="far" r={2} />
      <Blk x={-246} y={-128} w={306} h={18} g={g} tone="far" r={3} />
      {[-220, -140, -60, 20].map(x => (
        <rect key={x} className="zx-sky__mark" x={x} y="-70" width="48" height="70" rx="2" />
      ))}
      <Blk x={68} y={-168} w={110} h={168} g={g} tone="far" r={2} />
      <Row x={84} y={-148} n={3} gap={30} />
      <Row x={84} y={-112} n={3} gap={30} />
    </>
  ),
];

/* ════════════════════════════════════════════════════════════════════════
   04 · EDUCATION — a campus
   ════════════════════════════════════════════════════════════════════════ */
const EDUCATION = g => [
  // main hall and clock tower
  () => (
    <>
      <Blk x={-250} y={-152} w={210} h={152} g={g} tone="far" />
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -262 -152 L -145 -196 L -28 -152 Z" />
      <Blk x={-24} y={-258} w={74} h={258} g={g} tone="far" />
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -36 -258 L 13 -304 L 62 -258 Z" />
      <circle className="zx-sky__mark" cx="13" cy="-224" r="17" />
      <Blk x={68} y={-140} w={168} h={140} g={g} tone="far" />
      <Row x={-232} y={-132} n={6} gap={30} />
      <Row x={84} y={-120} n={5} gap={30} />
    </>
  ),
  // lecture dome
  () => (
    <>
      <Blk x={-232} y={-124} w={150} h={124} g={g} tone="far" />
      <Blk x={-66} y={-146} w={188} h={146} g={g} tone="far" />
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -40 -146 A 66 66 0 0 1 92 -146 Z" />
      <rect className="zx-blk__mast" x={23} y="-236" width="5" height="26" />
      <Blk x={148} y={-104} w={106} h={104} g={g} tone="far" />
      <Row x={-216} y={-104} n={4} gap={30} />
      <Row x={-50} y={-126} n={5} gap={30} />
    </>
  ),
  // library with a colonnade
  () => (
    <>
      <Blk x={-238} y={-176} w={244} h={176} g={g} tone="far" />
      <Blk x={-250} y={-192} w={268} h={18} g={g} tone="far" r={3} />
      {[-216, -166, -116, -66, -16].map(x => (
        <rect key={x} className="zx-blk__mast" x={x} y="-74" width="11" height="74" />
      ))}
      <Row x={-222} y={-160} n={7} gap={32} />
      <Row x={-222} y={-122} n={7} gap={32} />
      <Blk x={40} y={-118} w={130} h={118} g={g} tone="far" />
      <Row x={56} y={-98} n={4} gap={28} />
    </>
  ),
  // floodlit pitch and sports hall
  () => (
    <>
      <Blk x={-246} y={-116} w={190} h={116} g={g} tone="far" r={3} />
      <path className="zx-blk zx-blk--far" fill={`url(#${g})`} d="M -256 -116 A 105 52 0 0 1 -46 -116 Z" />
      {[-30, 210].map(x => (
        <React.Fragment key={x}>
          <rect className="zx-blk__mast" x={x} y="-230" width="6" height="230" />
          <rect className="zx-sky__mark" x={x - 20} y="-244" width="46" height="16" rx="3" />
        </React.Fragment>
      ))}
      <Blk x={40} y={-88} w={148} h={88} g={g} tone="far" r={3} />
      <Row x={-230} y={-96} n={5} gap={32} />
    </>
  ),
  // residences
  () => (
    <>
      <Blk x={-244} y={-168} w={104} h={168} g={g} tone="far" />
      <Blk x={-124} y={-206} w={104} h={206} g={g} tone="far" />
      <Blk x={-4} y={-152} w={104} h={152} g={g} tone="far" />
      <Blk x={116} y={-188} w={104} h={188} g={g} tone="far" />
      {[-228, -108, 12, 132].map((x, i) => (
        <React.Fragment key={x}>
          <Row x={x} y={-146 - i * 6} n={3} gap={26} />
          <Row x={x} y={-110 - i * 6} n={3} gap={26} />
          <Row x={x} y={-74 - i * 6} n={3} gap={26} />
        </React.Fragment>
      ))}
    </>
  ),
];

const KITS = { '01': HEALTH, '02': ENERGY, '03': INDUSTRIAL, '04': EDUCATION };

export function SkylineFar({ sectorId, index, gradientId }) {
  const kit = (KITS[sectorId] || HEALTH)(gradientId);
  const Draw = kit[index % kit.length];
  return <Draw />;
}

/* ── the deep layer ───────────────────────────────────────────────────────
   Massing only. No windows, no marks, no structure — deliberately denied the
   detail even though it would be trivial to add, because detail is what makes
   a far thing read as near. It is here to be a horizon, and to move slowest.

   Per sector only insofar as the PROFILE differs: towers for the hospital
   campus, cooling-tower curves for energy, long low sheds for the plant,
   pitched roofs for the campus. That is as much as survives at this weight,
   and it is enough for the eye to register the two landscapes as different
   places without ever looking at it directly. */
export function SkylineDeep({ sectorId, index }) {
  const seed = ((index + 1) * 2654435761) >>> 0;
  const rnd = k => ((seed >> (k * 3)) & 7) / 7;
  /* y is the top; every mass ends exactly on 0, because the floor plane is a
     different element BEHIND this one and anything below the line draws over
     it. */
  const M = (x, w, h) => (
    <rect className="zx-sky__deep" x={x} y={-h} width={w} height={h} />
  );

  if (sectorId === '02') {
    return (
      <>
        <path className="zx-sky__deep" d="M -300 0 L -282 -150 q 20 -17 40 0 L -222 0 Z" />
        <path className="zx-sky__deep" d="M -170 0 L -156 -118 q 15 -13 30 0 L -110 0 Z" />
        {M(-60, 150, 96 + rnd(1) * 40)}
        {M(120, 96, 130 + rnd(2) * 40)}
      </>
    );
  }
  if (sectorId === '03') {
    return (
      <>
        {M(-300, 240, 92 + rnd(0) * 26)}
        {M(-46, 34, 210 + rnd(1) * 30)}
        {M(6, 196, 116 + rnd(2) * 34)}
        {M(220, 110, 84 + rnd(3) * 30)}
      </>
    );
  }
  if (sectorId === '04') {
    return (
      <>
        <path className="zx-sky__deep" d="M -300 0 L -300 -108 L -190 -152 L -80 -108 L -80 0 Z" />
        {M(-56, 86, 176 + rnd(1) * 34)}
        <path className="zx-sky__deep" d="M 56 0 L 56 -96 L 150 -138 L 244 -96 L 244 0 Z" />
      </>
    );
  }
  return (
    <>
      {M(-300, 126, 138 + rnd(0) * 40)}
      {M(-160, 104, 196 + rnd(1) * 44)}
      {M(-42, 140, 116 + rnd(2) * 36)}
      {M(116, 112, 166 + rnd(3) * 40)}
    </>
  );
}
